import api from './api';

/**
 * Service for interacting with completed survey endpoints
 */
const completedSurveyService = {
  /**
   * Get all completed surveys for a participant with optional filters
   * @param {string} participantId - The participant ID
   * @param {boolean} bypassCache - Whether to bypass cache when fetching data
   * @param {string} simulatedDate - Optional simulated date for testing (format: YYYY-MM-DD HH:MM TZ)
   * @param {Object} filters - Optional filters
   * @returns {Promise<Object>} - The API response
   */
  getCompletedSurveys: async (participantId, bypassCache = true, simulatedDate = null, filters = {}, includeQuestions = false) => {
    try {
      console.log(`Fetching completed surveys for participant ${participantId}${simulatedDate ? ', with simulated date: ' + simulatedDate : ''}`);
      
      // Build query parameters
      const queryParams = new URLSearchParams();
      queryParams.append('participant_id', participantId);
      
      // By default, exclude questions to optimize payload size
      queryParams.append('include_questions', includeQuestions ? 'true' : 'false');
      
      // Add simulated date parameter if provided
      if (simulatedDate) {
        queryParams.append('simulated_date', simulatedDate);
      }
      
      // Add cache busting if needed
      if (bypassCache) {
        queryParams.append('bypass_cache', 'true');
        queryParams.append('ts', new Date().getTime()); // Add timestamp to force fresh data
      }
      
      // Add optional filters if provided
      if (filters.completed_after) {
        queryParams.append('completed_after', filters.completed_after);
      }
      if (filters.completed_before) {
        queryParams.append('completed_before', filters.completed_before);
      }
      if (filters.survey) {
        queryParams.append('survey', filters.survey);
      }
      if (filters.schedule_type) {
        queryParams.append('schedule_type', filters.schedule_type);
      }
      if (filters.status) {
        queryParams.append('status', filters.status);
      }
      if (filters.ordering) {
        queryParams.append('ordering', filters.ordering);
      }
      
      console.log(`Making API request to: /api/completed-surveys/?${queryParams.toString()}`);
      const response = await api.get(`/api/completed-surveys/?${queryParams.toString()}`);
      return response.data; // Return the full response with pagination info and results array
    } catch (error) {
      console.error('Error fetching completed surveys:', error);
      throw error;
    }
  },

  /**
   * Get completion statistics for a participant
   * @param {string} participantId - The participant ID
   * @returns {Promise<Object>} - The API response
   */
  getCompletionStats: async (participantId) => {
    try {
      const response = await api.get(`/api/completed-surveys/completion_stats/?participant_id=${participantId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching completion stats:', error);
      throw error;
    }
  },

  /**
   * Get participant history
   * @param {string} participantId - The participant ID
   * @returns {Promise<Object>} - The API response
   */
  getParticipantHistory: async (participantId) => {
    try {
      const response = await api.get(`/api/completed-surveys/participant_history/?participant_id=${participantId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching participant history:', error);
      throw error;
    }
  },

  /**
   * Get detailed information about a specific completed survey
   * @param {string} surveyId - The completed survey ID
   * @returns {Promise<Object>} - The API response with full survey details and responses
   */
  getCompletedSurveyDetails: async (surveyId) => {
    try {
      const response = await api.get(`/api/completed-surveys/${surveyId}/details/`);
      const data = response.data;

      // Check if questions array is empty but we have responses
      if ((!data.questions || data.questions.length === 0) && data.responses && Object.keys(data.responses).length > 0) {
        console.log('Fetching question details for response IDs');
        
        // Get all question IDs from the responses
        const questionIds = Object.keys(data.responses);
        
        // Create questions array with question details
        const questions = await Promise.all(
          questionIds.map(async (questionId) => {
            try {
              // Fetch question details
              const questionDetails = await completedSurveyService.getQuestionDetails(questionId);
              
              return {
                id: questionId,
                text: questionDetails?.text || `Question ${questionId}`,
                type: questionDetails?.type || 'text',
                options: questionDetails?.options || {},
                answer: data.responses[questionId]
              };
            } catch (err) {
              console.error(`Error fetching details for question ${questionId}:`, err);
              // Return a fallback question object
              return {
                id: questionId,
                text: `Question ${questionId}`,
                type: determineQuestionType(data.responses[questionId].answer),
                answer: data.responses[questionId]
              };
            }
          })
        );
        
        data.questions = questions;
      }
      
      return data;
    } catch (error) {
      console.error('Error fetching completed survey details:', error);
      throw error;
    }
  },

  /**
   * Get details for a specific question - enhanced to use surveys/questions endpoint
   * @param {string} questionId - The question ID
   * @returns {Promise<Object>} - The question details
   */
  getQuestionDetails: async (questionId) => {
    try {
      // Use the survey questions API endpoint format
      const response = await api.get(`/api/surveys/questions/${questionId}/`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching question details for ID ${questionId}:`, error);
      throw error;
    }
  },

  // Check for missed surveys for all participants in a study
  checkMissedSurveysForStudy: async (studyId) => {
    try {
      console.log(`Checking missed surveys for all participants in study: ${studyId}`);
      const url = `/api/studies/${studyId}/check-missed-surveys/`;
      console.log(`API URL: ${url}`);
      
      const response = await api.post(url);
      console.log('Check study missed surveys response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error checking missed surveys for study:', error);
      console.error('Error details:', error.response ? error.response.data : 'No response data');
      console.error('Status code:', error.response ? error.response.status : 'Unknown');
      
      // Don't throw the error as this is a background check that shouldn't break the UI
      return { 
        error: error.message,
        details: error.response ? error.response.data : null,
        status: error.response ? error.response.status : null
      };
    }
  },
};

/**
 * Helper function to determine question type based on answer value
 * @param {any} answerValue - The answer value
 * @returns {string} - The inferred question type
 */
function determineQuestionType(answerValue) {
  if (typeof answerValue === 'number') {
    return 'numeric';
  } else if (answerValue === 'true' || answerValue === 'false' || answerValue === true || answerValue === false) {
    return 'yes_no';
  } else if (answerValue && typeof answerValue === 'string' && answerValue.includes('-') && answerValue.split('-').length === 3) {
    // Simple pattern matching for dates (YYYY-MM-DD)
    return 'date';
  }
  
  return 'text';
}

export default completedSurveyService;
